// Cross-session persistence for the BI SPA's TanStack Query cache, so a
// reopen paints the last-known numbers at once (dimmed via `bi-query.ts`'s
// `refreshing` flag) while the real request revalidates.
//
// Hand-rolled on `dehydrate`/`hydrate` (already in `@tanstack/react-query`)
// rather than `@tanstack/react-query-persist-client`: no new dependency.
//
// The ceiling is the SAME 3 hours as the backend's `RESULT_TTL_SECONDS`
// (`service/bi/core/result_storage.py`): an older local result would paint a
// number the server can no longer serve without a fresh scan. Move one, move
// the other.
import { dehydrate, hydrate, type DehydratedState, type QueryClient } from "@tanstack/react-query";

type PersistedQuery = DehydratedState["queries"][number];

/** Matches `RESULT_TTL_SECONDS` in `service/bi/core/result_storage.py`. */
export const PERSIST_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/** localStorage is a ~5MB per-origin budget shared with everything else this
 *  origin stores, and a single table chart's result can run to megabytes
 *  (`POSTGRES_OFFLOAD_MAX_BYTES` is 5MB server-side). Persisting is an
 *  optimization, never a correctness requirement, so the budget is spent
 *  newest-first and whatever does not fit is simply not persisted — those
 *  charts re-fetch on the next open exactly as they do today. */
export const PERSIST_MAX_BYTES = 2_000_000;

const STORAGE_KEY = "bi:query-cache";
const ENVELOPE_VERSION = 1;
/** Coalesces the burst of cache events one dashboard load produces (one per
 *  chart result) into a single write. */
const WRITE_DEBOUNCE_MS = 1_000;

export interface PersistEnvelope {
  version: number;
  savedAt: number;
  queries: PersistedQuery[];
}

export interface PruneOptions {
  now: number;
  maxAgeMs?: number;
  maxBytes?: number;
}

export interface PruneResult {
  kept: PersistedQuery[];
  /** How many fresh-enough queries were dropped purely because the byte
   *  budget ran out. Surfaced (not swallowed) so a board that never persists
   *  is diagnosable rather than mysteriously slow — root doctrine §1.12's
   *  "a cap is never silent", in the one place a browser has no metrics
   *  recorder to reach for. */
  droppedForSize: number;
}

function entryBytes(query: PersistedQuery): number {
  try {
    return JSON.stringify(query).length;
  } catch {
    // A non-serializable entry (should not happen for wire data) counts as
    // over budget rather than throwing the whole save away.
    return Number.POSITIVE_INFINITY;
  }
}

/** Newest-first, age-filtered, byte-budgeted. Pure — the storage wrapper
 *  below is the only side-effecting part of this module. */
export function prunePersistableQueries(
  queries: readonly PersistedQuery[],
  { now, maxAgeMs = PERSIST_MAX_AGE_MS, maxBytes = PERSIST_MAX_BYTES }: PruneOptions,
): PruneResult {
  const fresh = queries.filter((q) => {
    const updatedAt = q.state.dataUpdatedAt;
    return typeof updatedAt === "number" && updatedAt > 0 && now - updatedAt <= maxAgeMs;
  });
  // Newest first: if the budget cannot hold everything, the tiles a reader
  // just looked at are the ones worth keeping.
  const ordered = [...fresh].sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);

  const kept: PersistedQuery[] = [];
  let used = 0;
  let droppedForSize = 0;
  for (const query of ordered) {
    const size = entryBytes(query);
    if (used + size > maxBytes) {
      droppedForSize += 1;
      continue;
    }
    used += size;
    kept.push(query);
  }
  return { kept, droppedForSize };
}

/** Parse + validate a stored envelope, returning the still-usable queries.
 *  `null` for anything absent, unparseable, from another version, or wholly
 *  past the age ceiling — every one of which is a plain cold start, not an
 *  error worth surfacing to a reader. Pure. */
export function readPersistedQueries(
  raw: string | null,
  { now, maxAgeMs = PERSIST_MAX_AGE_MS }: { now: number; maxAgeMs?: number },
): PersistedQuery[] | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const envelope = parsed as Partial<PersistEnvelope>;
  if (envelope.version !== ENVELOPE_VERSION) return null;
  if (typeof envelope.savedAt !== "number" || now - envelope.savedAt > maxAgeMs) return null;
  if (!Array.isArray(envelope.queries)) return null;
  // Re-apply the age filter per entry: the envelope's own `savedAt` only
  // bounds the WRITE, and one write carries results of different ages.
  return prunePersistableQueries(envelope.queries, { now, maxAgeMs }).kept;
}

export interface PersistStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeStorage(): PersistStorage | null {
  try {
    // Private-mode Safari and a disabled-storage profile both throw on
    // access, not on use — persistence is optional, so a failure here is a
    // silent downgrade to the memory-only behaviour we had before.
    const probe = window.localStorage;
    probe.setItem(`${STORAGE_KEY}:probe`, "1");
    probe.removeItem(`${STORAGE_KEY}:probe`);
    return probe;
  } catch {
    return null;
  }
}

/** Hydrate the client from storage, then keep storage in step with the
 *  cache. Returns a teardown function. Safe to call when storage is
 *  unavailable (it no-ops). */
export function installQueryCachePersistence(
  client: QueryClient,
  storage: PersistStorage | null = safeStorage(),
): () => void {
  if (!storage) return () => {};

  const restored = readPersistedQueries(storage.getItem(STORAGE_KEY), { now: Date.now() });
  if (restored && restored.length > 0) {
    hydrate(client, { mutations: [], queries: restored } satisfies DehydratedState);
  }

  const save = (): void => {
    const state = dehydrate(client, {
      // Successful reads only — an error or a pending fetch must never
      // outlive the session that produced it.
      shouldDehydrateQuery: (query) => query.state.status === "success",
      shouldDehydrateMutation: () => false,
    });
    const { kept, droppedForSize } = prunePersistableQueries(state.queries, { now: Date.now() });
    if (droppedForSize > 0) {
      // eslint-disable-next-line no-console -- see PruneResult.droppedForSize
      console.warn(
        `[bi] ${droppedForSize} cached chart result(s) too large to persist; they will re-fetch on the next open.`,
      );
    }
    const envelope: PersistEnvelope = {
      version: ENVELOPE_VERSION,
      savedAt: Date.now(),
      queries: kept,
    };
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(envelope));
    } catch {
      // Quota exceeded despite the budget (another tab, another app on this
      // origin). Drop what we had rather than leaving a half-written entry.
      try {
        storage.removeItem(STORAGE_KEY);
      } catch {
        /* storage went away entirely — nothing to clean up */
      }
    }
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const scheduleSave = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(save, WRITE_DEBOUNCE_MS);
  };
  const flush = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    save();
  };

  const unsubscribe = client.getQueryCache().subscribe(scheduleSave);
  // `pagehide` rather than `beforeunload`: the latter is unreliable on
  // mobile and disables the bfcache. `visibilitychange` catches a tab the
  // reader switches away from and never closes.
  const onHide = (): void => {
    if (document.visibilityState === "hidden") flush();
  };
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", onHide);

  return () => {
    if (timer !== undefined) clearTimeout(timer);
    unsubscribe();
    window.removeEventListener("pagehide", flush);
    document.removeEventListener("visibilitychange", onHide);
  };
}
